import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Search, Building2, Users, X, RefreshCw, MoreVertical, User } from 'lucide-react';
import apiService from '../../services/api';

const DepartmentManagement = () => {
  const { t } = useTranslation();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedDeptMembers, setSelectedDeptMembers] = useState([]);
  const [selectedDeptName, setSelectedDeptName] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await apiService.request('/admin/departments');
      setDepartments(data);
    } catch (err) {
      console.error('Failed to load departments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDept(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      isActive: true
    });
    setError('');
    setShowModal(true);
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      isActive: dept.isActive
    });
    setError('');
    setShowModal(true);
  };

  const handleDelete = async (dept) => {
    if (!window.confirm(t('department.confirmDelete', { name: dept.name }))) return;
    
    try {
      await apiService.request(`/admin/departments/${dept.id}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      alert(t('department.deleteFailed') + ': ' + (err.message || t('admin.common.error')));
    }
  };

  const handleShowMembers = async (dept) => {
    setMenuOpenId(null);
    setSelectedDeptName(dept.name);
    setLoadingMembers(true);
    setShowMembersModal(true);
    
    try {
      const members = await apiService.request(`/admin/departments/${dept.id}/members`);
      setSelectedDeptMembers(members);
    } catch (err) {
      console.error('Failed to load members:', err);
      setSelectedDeptMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingDept) {
        await apiService.request(`/admin/departments/${editingDept.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
      } else {
        await apiService.request('/admin/departments', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message || t('admin.common.error'));
    } finally {
      setSaving(false);
    }
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <RefreshCw size={24} color="#7c3aed" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'white',
          borderRadius: '8px',
          padding: '8px 12px',
          flex: 1,
          maxWidth: '300px',
          border: '1px solid #e5e7eb'
        }}>
          <Search size={16} color="#9ca3af" />
          <input
            type="text"
            placeholder={t('department.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              marginLeft: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
        <button
          onClick={handleAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: '#7c3aed',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <Plus size={16} />
          {t('department.newDepartment')}
        </button>
      </div>

      {/* Department List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filteredDepartments.map(dept => (
          <div
            key={dept.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}
          >
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: dept.isActive 
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              marginRight: '12px'
            }}>
              <Building2 size={20} />
            </div>
            
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '500', color: '#1f2937' }}>{dept.name}</span>
                <span style={{
                  padding: '2px 6px',
                  background: '#f3f4f6',
                  color: '#6b7280',
                  fontSize: '10px',
                  borderRadius: '4px',
                  fontWeight: '500'
                }}>
                  {dept.code}
                </span>
                {!dept.isActive && (
                  <span style={{
                    padding: '2px 6px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    fontSize: '10px',
                    borderRadius: '4px',
                    fontWeight: '600'
                  }}>
                    {t('department.inactive')}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Users size={12} />
                {dept.memberCount} {t('department.members')}
                {dept.description && ` · ${dept.description}`}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => handleEdit(dept)}
                style={{
                  padding: '8px',
                  background: '#f3f4f6',
                  borderRadius: '6px'
                }}
              >
                <Edit size={16} color="#6b7280" />
              </button>
              <button
                onClick={() => handleDelete(dept)}
                style={{
                  padding: '8px',
                  background: '#fef2f2',
                  borderRadius: '6px'
                }}
              >
                <Trash2 size={16} color="#dc2626" />
              </button>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setMenuOpenId(menuOpenId === dept.id ? null : dept.id)}
                  style={{
                    padding: '8px',
                    background: menuOpenId === dept.id ? '#e5e7eb' : '#f3f4f6',
                    borderRadius: '6px'
                  }}
                >
                  <MoreVertical size={16} color="#6b7280" />
                </button>
                {menuOpenId === dept.id && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 100,
                    minWidth: '140px',
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={() => handleShowMembers(dept)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '10px 12px',
                        background: 'white',
                        color: '#1f2937',
                        fontSize: '13px',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      <Users size={14} />
                      {t('department.showMembers')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredDepartments.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <Building2 size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>{t('department.notFound')}</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontWeight: '600' }}>
                {editingDept ? t('department.editDepartment') : t('department.newDepartment')}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ padding: '4px' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '16px' }}>
              {error && (
                <div style={{
                  padding: '12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  color: '#dc2626',
                  fontSize: '13px',
                  marginBottom: '16px'
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('department.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder={t('department.namePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('department.code')} *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                  required
                  placeholder={t('department.codePlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                  {t('department.codeHelp')}
                </p>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {t('department.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('department.descriptionPlaceholder')}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px' }}>{t('department.active')}</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {t('department.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: saving ? '#9ca3af' : '#7c3aed',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {saving ? t('department.saving') : t('department.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {showMembersModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '400px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                  {selectedDeptName}
                </h3>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                  {t('department.membersTitle')}
                </p>
              </div>
              <button
                onClick={() => setShowMembersModal(false)}
                style={{ padding: '4px' }}
              >
                <X size={20} color="#6b7280" />
              </button>
            </div>
            
            <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
              {loadingMembers ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <RefreshCw size={24} color="#7c3aed" style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              ) : selectedDeptMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                  <User size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: '14px' }}>{t('department.noMembers')}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {selectedDeptMembers.map(member => (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        gap: '12px'
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: member.isActive 
                          ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                          : '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '12px'
                      }}>
                        {member.fullName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', fontSize: '14px', color: '#1f2937' }}>
                          {member.fullName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          @{member.username}
                        </div>
                      </div>
                      {!member.isActive && (
                        <span style={{
                          padding: '2px 6px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          fontSize: '10px',
                          borderRadius: '4px',
                          fontWeight: '600'
                        }}>
                          {t('department.inactive')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => setShowMembersModal(false)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {t('department.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
